import {
  MethodForModel,
  ModelName,
  ParamsForMethod,
  SessionMethodName,
  SessionMethodParams,
  SessionQueryKey,
  TellescopeQueryKey,
  TellescopeQueryKeyFilter,
} from "./types";

/**
 * Builds a TanStack Query key for Tellescope API queries.
 * Query keys are used for caching, invalidation, and refetching.
 *
 * The key structure is:
 * - Without request params: `[model, method]`
 * - With request params: `[model, method, request]`
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The API method for the specified model (e.g., "getOne", "getSome")
 *
 * @param options - Configuration object for building the query key
 * @param options.model - The name of the Tellescope model
 * @param options.method - The API method to call on the model
 * @param options.request - Optional parameters for the method call
 *
 * @returns A structured query key array that uniquely identifies this query
 *
 * @example
 * ```ts
 * // Simple query key without params
 * buildTellescopeQueryKey({ model: "users", method: "getOne" });
 * // Returns: ["users", "getOne"]
 *
 * // Query key with params
 * buildTellescopeQueryKey({
 *   model: "users",
 *   method: "getSome",
 *   request: { filter: { role: "admin" } }
 * });
 * // Returns: ["users", "getSome", { filter: { role: "admin" } }]
 * ```
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Recursively removes object entries whose value is `undefined`, leaving the value unchanged otherwise.
 * `null` is preserved (it is a meaningful filter value) as are non-plain objects such as `Date`.
 */
const stripUndefinedFromNestedObjects = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripUndefinedFromNestedObjects);
  }
  if (isPlainObject(value)) {
    return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, val]) => {
      if (val !== undefined) {
        acc[key] = stripUndefinedFromNestedObjects(val);
      }
      return acc;
    }, {});
  }
  return value;
};

/**
 * Normalizes a query request before it is stored in (and read back from) the query key.
 *
 * Requests are serialized to JSON before being sent, and `JSON.stringify` drops `undefined`-valued
 * keys — so the backend never filters on them. The query key, however, retains the request object
 * verbatim, so an `{ mdbFilter: { tags: undefined } }` entry would otherwise survive into the key and
 * be read back by cache-matching logic (`modelMatchesGetSomeRequest`) as a literal "tags must be
 * undefined" condition, incorrectly dropping freshly-updated records from cached lists.
 *
 * Only entries nested inside objects are stripped — the request's own top-level entries are left
 * intact so the key shape stays stable.
 */
const normalizeQueryKeyRequest = <Model extends ModelName, Method extends MethodForModel<Model>>(
  request: Partial<ParamsForMethod<Model, Method>>
): Partial<ParamsForMethod<Model, Method>> => {
  if (!isPlainObject(request)) return request;
  return Object.entries(request).reduce<Record<string, unknown>>((acc, [key, val]) => {
    acc[key] = stripUndefinedFromNestedObjects(val);
    return acc;
  }, {}) as Partial<ParamsForMethod<Model, Method>>;
};

export const buildTellescopeQueryKey = <Model extends ModelName, Method extends MethodForModel<Model>>(options: {
  model: Model;
  method: Method | `${Method}-count`;
  request?: Partial<ParamsForMethod<Model, Method>>;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}): TellescopeQueryKey<Model, Method> => {
  const { model, method, request, requestKeysToOmitFromQueryKey } = options;

  if (request === undefined) {
    return [model, method];
  }

  const filteredRequest =
    typeof request === "object" && request !== null && requestKeysToOmitFromQueryKey?.length
      ? (Object.fromEntries(
          Object.entries(request).filter(([key]) => !(requestKeysToOmitFromQueryKey as string[]).includes(key))
        ) as Partial<ParamsForMethod<Model, Method>>)
      : request;

  return [model, method, normalizeQueryKeyRequest<Model, Method>(filteredRequest)];
};

/**
 * Builds a TanStack Query key for targeting Tellescope API queries via filters.
 *
 * The key structure is:
 * - Without request params: `[model, method]`
 * - With request params: `[model, method, Partial<request>]`
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The API method for the specified model (e.g., "getOne", "getSome")
 *
 * @param options - Configuration object for building the query key
 * @param options.model - The name of the Tellescope model
 * @param options.method - The API method to call on the model
 * @param options.request - Optional partial parameters for the method call
 *
 * @returns A structured query key array
 *
 * @example
 * ```ts
 * // Simple query key without params
 * buildTellescopeQueryKey({ model: "users", method: "getOne" });
 * // Returns: ["users", "getOne"]
 *
 * // Query key with params
 * buildTellescopeQueryKey({
 *   model: "users",
 *   method: "getSome",
 *   request: { filter: { role: "admin" } }
 * });
 * // Returns: ["users", "getSome", { filter: { role: "admin" } }]
 * ```
 */
export const buildTellescopeQueryKeyFilter = <Model extends ModelName, Method extends MethodForModel<Model>>(options: {
  model: Model;
  method: Method;
  request?: Partial<ParamsForMethod<Model, Method>>;
}): TellescopeQueryKeyFilter<Model, Method> => {
  const { model, method, request } = options;

  if (request === undefined) {
    return [model, method];
  }

  return [model, method, request];
};

/**
 * Extracts the model name from a Tellescope query key.
 *
 * @template Model - The Tellescope model name
 * @template Method - The API method for the specified model
 *
 * @param queryKey - The query key array to extract from
 * @returns The model name from the first element of the query key
 *
 * @example
 * ```ts
 * const queryKey = buildTellescopeQueryKey({ model: "users", method: "getSome" });
 * const model = extractModelFromQueryKey(queryKey);
 * // Returns: "users"
 * ```
 */
export const extractModelFromQueryKey = <Model extends ModelName, Method extends MethodForModel<Model>>(
  queryKey: TellescopeQueryKey<Model, Method>
): Model => queryKey[0];

/**
 * Extracts the method name from a Tellescope query key.
 *
 * @template Model - The Tellescope model name
 * @template Method - The API method for the specified model
 *
 * @param queryKey - The query key array to extract from
 * @returns The method name from the second element of the query key
 *
 * @example
 * ```ts
 * const queryKey = buildTellescopeQueryKey({ model: "users", method: "getSome" });
 * const method = extractMethodFromQueryKey(queryKey);
 * // Returns: "getSome"
 * ```
 */
export const extractMethodFromQueryKey = <Model extends ModelName, Method extends MethodForModel<Model>>(
  queryKey: TellescopeQueryKey<Model, Method>
): Method | `${Method}-count` => queryKey[1];

/**
 * Extracts the request parameters from a Tellescope query key.
 *
 * @template Model - The Tellescope model name
 * @template Method - The API method for the specified model
 *
 * @param queryKey - The query key array to extract from
 * @returns The request parameters from the third element of the query key, or undefined if not present
 *
 * @example
 * ```ts
 * const queryKey = buildTellescopeQueryKey({
 *   model: "users",
 *   method: "getSome",
 *   request: { filter: { role: "admin" } }
 * });
 * const request = extractRequestFromQueryKey(queryKey);
 * // Returns: { filter: { role: "admin" } }
 *
 * const queryKeyWithoutRequest = buildTellescopeQueryKey({ model: "users", method: "getSome" });
 * const noRequest = extractRequestFromQueryKey(queryKeyWithoutRequest);
 * // Returns: undefined
 * ```
 */
export const extractRequestFromQueryKey = <Model extends ModelName, Method extends MethodForModel<Model>>(
  queryKey: TellescopeQueryKey<Model, Method>
): Partial<ParamsForMethod<Model, Method>> | undefined => queryKey[2];

// ============================================================================
// SESSION QUERY KEY UTILITIES
// ============================================================================

/**
 * Builds a TanStack Query key for Tellescope session queries.
 * Query keys are used for caching, invalidation, and refetching.
 *
 * The key structure is:
 * - Without request params: `["session", method]`
 * - With request params: `["session", method, request]`
 *
 * @template Method - The session method name (e.g., "sync", "test_authenticated")
 *
 * @param options - Configuration object for building the query key
 * @param options.method - The session method to call
 * @param options.request - Optional parameters for the method call
 *
 * @returns A structured query key array that uniquely identifies this query
 *
 * @example
 * ```ts
 * // Simple query key without params
 * buildSessionQueryKey({ method: "test_authenticated" });
 * // Returns: ["session", "test_authenticated"]
 *
 * // Query key with params
 * buildSessionQueryKey({
 *   method: "sync",
 *   request: { from: new Date("2024-01-01") }
 * });
 * // Returns: ["session", "sync", { from: Date(...) }]
 * ```
 */
export const buildSessionQueryKey = <Method extends SessionMethodName>(options: {
  method: Method;
  request?: Partial<SessionMethodParams<Method>>;
  requestKeysToOmitFromQueryKey?: SessionMethodParams<Method> extends Record<string, unknown>
    ? (keyof SessionMethodParams<Method>)[]
    : never;
}): SessionQueryKey<Method> => {
  const { method, request, requestKeysToOmitFromQueryKey } = options;

  if (request === undefined) {
    return ["session", method];
  }

  const filteredRequest =
    typeof request === "object" && request !== null && requestKeysToOmitFromQueryKey?.length
      ? (Object.fromEntries(
          Object.entries(request).filter(([key]) => !(requestKeysToOmitFromQueryKey as string[]).includes(key))
        ) as Partial<typeof request>)
      : request;

  return ["session", method, filteredRequest];
};

/**
 * Extracts the method name from a session query key.
 *
 * @template Method - The session method name
 *
 * @param queryKey - The query key array to extract from
 * @returns The method name from the second element of the query key
 *
 * @example
 * ```ts
 * const queryKey = buildSessionQueryKey({ method: "sync", request: { from: new Date() } });
 * const method = extractMethodFromSessionQueryKey(queryKey);
 * // Returns: "sync"
 * ```
 */
export const extractMethodFromSessionQueryKey = <Method extends SessionMethodName>(
  queryKey: SessionQueryKey<Method>
): Method => queryKey[1];

/**
 * Extracts the request parameters from a session query key.
 *
 * @template Method - The session method name
 *
 * @param queryKey - The query key array to extract from
 * @returns The request parameters from the third element of the query key, or undefined if not present
 *
 * @example
 * ```ts
 * const queryKey = buildSessionQueryKey({
 *   method: "sync",
 *   request: { from: new Date("2024-01-01") }
 * });
 * const request = extractRequestFromSessionQueryKey(queryKey);
 * // Returns: { from: Date(...) }
 *
 * const queryKeyWithoutRequest = buildSessionQueryKey({ method: "test_authenticated" });
 * const noRequest = extractRequestFromSessionQueryKey(queryKeyWithoutRequest);
 * // Returns: undefined
 * ```
 */
export const extractRequestFromSessionQueryKey = <Method extends SessionMethodName>(
  queryKey: SessionQueryKey<Method>
): Partial<SessionMethodParams<Method>> | undefined => queryKey[2];
